import { useState, useRef, useEffect } from 'react'
import clsx from 'clsx'
import type { TranscriptWord } from '../types'

interface WordPopupProps {
  word: TranscriptWord
  segmentIndex: number
  wordIndex: number
  onCorrect: (segmentIndex: number, wordIndex: number, correctedTo: string) => void
  onClose: () => void
}

export default function WordPopup({
  word,
  segmentIndex,
  wordIndex,
  onCorrect,
  onClose,
}: WordPopupProps) {
  const [customValue, setCustomValue] = useState('')
  const [showCustomInput, setShowCustomInput] = useState(false)
  const popupRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  // Focus input when showing custom input
  useEffect(() => {
    if (showCustomInput && inputRef.current) {
      inputRef.current.focus()
    }
  }, [showCustomInput])

  const handleSuggestionClick = (suggestion: string) => {
    onCorrect(segmentIndex, wordIndex, suggestion)
    onClose()
  }

  const handleCustomSubmit = () => {
    if (customValue.trim()) {
      onCorrect(segmentIndex, wordIndex, customValue.trim())
      onClose()
    }
  }

  const getFlagLabel = () => {
    if (!word.flags) return null
    if (word.flags.low_confidence) return 'Низкая точность'
    if (word.flags.unknown_term) return 'Неизвестный термин'
    if (word.flags.name_variant) return 'Вариант имени'
    if (word.flags.abbreviation) return 'Аббревиатура'
    return null
  }

  const flagLabel = getFlagLabel()

  return (
    <div
      ref={popupRef}
      className="absolute z-50 bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[200px] max-w-[300px]"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="px-3 pb-2 border-b border-gray-100">
        <div className="font-medium text-gray-800 break-words">
          "{word.corrected_to || word.text}"
        </div>
        {flagLabel && (
          <div className="text-xs text-amber-600 mt-1">{flagLabel}</div>
        )}
        {word.confidence !== null && word.confidence < 0.7 && (
          <div className="text-xs text-gray-400 mt-0.5">
            Точность: {Math.round(word.confidence * 100)}%
          </div>
        )}
      </div>

      {/* Suggestions */}
      {word.suggestions && word.suggestions.length > 0 && (
        <div className="py-1">
          <div className="px-3 py-1 text-xs text-gray-500 uppercase tracking-wide">
            Варианты
          </div>
          {word.suggestions.map((suggestion, idx) => (
            <button
              key={idx}
              onClick={() => handleSuggestionClick(suggestion)}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-primary-50 hover:text-primary-700 transition"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}

      {/* Custom input */}
      <div className="pt-1 border-t border-gray-100">
        {showCustomInput ? (
          <div className="px-3 py-2">
            <input
              ref={inputRef}
              type="text"
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCustomSubmit()
                if (e.key === 'Escape') onClose()
              }}
              placeholder="Введите исправление..."
              className="w-full px-2 py-1.5 text-sm border rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => setShowCustomInput(false)}
                className="flex-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
              >
                Отмена
              </button>
              <button
                onClick={handleCustomSubmit}
                disabled={!customValue.trim()}
                className={clsx(
                  'flex-1 px-2 py-1 text-xs rounded',
                  customValue.trim()
                    ? 'bg-primary-500 text-white hover:bg-primary-600'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                )}
              >
                Применить
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowCustomInput(true)}
            className="w-full text-left px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
          >
            Ввести свой вариант...
          </button>
        )}
      </div>

      {/* Restore original */}
      {word.corrected_to && (
        <div className="pt-1 border-t border-gray-100">
          <button
            onClick={() => handleSuggestionClick(word.text)}
            className="w-full text-left px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100"
          >
            Вернуть: "{word.text}"
          </button>
        </div>
      )}
    </div>
  )
}
